// ContractReaderPanel — the AI contract reader on a deal (add-on #5, 18 Oct 2026).
// Pick the signed contract PDF uploaded to this deal; the AI reads it and proposes the
// price, the contract date and every deadline, each beside the deal's current value with the
// contract's own words and page. Nothing changes until values are ticked and accepted.
// API: /api/contract-reader (routes/contractReader.js).
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';

const API = '/api/contract-reader';
const usDate = (ymd) => (ymd ? `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}/${ymd.slice(0, 4)}` : '—');
const show = (p, v) => (v == null || v === '' ? '—' : p.key === 'final_price' ? Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : usDate(String(v)));
const BASIS = { business: 'business days', calendar: 'calendar days', calendar_roll: 'calendar days, rolling past weekends/holidays' };

export default function ContractReaderPanel({ transactionId, onApplied }) {
    const api = useApiClient();
    const user = getCurrentUser();
    const isBroker = !!(user && user.isManagingBroker);
    const [info, setInfo] = useState(null);
    const [docId, setDocId] = useState('');
    const [reading, setReading] = useState(null);
    const [picked, setPicked] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`${API}/transactions/${transactionId}`);
            setInfo(r);
            if (r.documents.length) setDocId((d) => d || String(r.documents[0].document_id));
        } catch (e) { setMsg({ bad: true, text: e.message }); }
    }, [api, transactionId]);
    useEffect(() => { load(); }, [load]);

    const take = (r) => {
        setReading(r);
        setPicked(new Set((r.proposals || []).filter((p) => p.proposed != null && String(p.proposed) !== String(p.current)).map((p) => p.key)));
    };
    const read = async () => {
        setBusy(true); setMsg(null); setReading(null);
        try { take(await api.post(`${API}/transactions/${transactionId}/read`, { document_id: Number(docId) })); load(); }
        catch (e) { setMsg({ bad: true, text: e.message }); load(); } finally { setBusy(false); }
    };
    const apply = async () => {
        setBusy(true); setMsg(null);
        try {
            const r = await api.post(`${API}/extractions/${reading.extraction_id}/apply`, { keys: [...picked] });
            setMsg({ text: `Accepted ${r.applied.length} value(s). The milestones below are updated.` });
            setReading(null); load(); if (onApplied) onApplied();
        } catch (e) { setMsg({ bad: true, text: e.message }); } finally { setBusy(false); }
    };
    const discard = async () => {
        try { await api.post(`${API}/extractions/${reading.extraction_id}/discard`, {}); setReading(null); setMsg({ text: 'Reading discarded. Nothing was changed.' }); load(); }
        catch (e) { setMsg({ bad: true, text: e.message }); }
    };
    const reopen = async (eid) => { try { take(await api.get(`${API}/extractions/${eid}`)); } catch (e) { setMsg({ bad: true, text: e.message }); } };
    const toggleOn = async (on) => { try { await api.put(`${API}/settings`, { enabled: on }); load(); } catch (e) { setMsg({ bad: true, text: e.message }); } };

    if (!info) return null;
    const d = reading && reading.data;
    return (
        <div className="bg-white p-6 rounded-lg shadow-md space-y-4">
            <div className="flex flex-wrap justify-between items-start gap-2">
                <div>
                    <h2 className="text-xl font-semibold text-slate-800">Read the contract (AI)</h2>
                    <p className="text-sm text-slate-500">The AI reads the signed contract and proposes the price, contract date and deadlines. You check each one; nothing changes until you accept it.</p>
                </div>
                {isBroker && <button onClick={() => toggleOn(!info.enabled)} className="text-xs text-slate-500 underline">{info.enabled ? 'Switch the contract reader off' : ''}</button>}
            </div>
            {msg && <p className={`text-sm ${msg.bad ? 'text-red-700' : 'text-emerald-700'}`}>{msg.text}</p>}
            {!info.enabled ? (
                <div className="text-sm text-slate-600">
                    The contract reader is switched off. {isBroker
                        ? <button onClick={() => toggleOn(true)} className="ml-1 px-3 py-1 rounded bg-slate-800 text-white text-sm">Switch it on</button>
                        : 'The managing broker can switch it on.'}
                    <p className="text-xs text-slate-400 mt-1">The PDF is sent to the firm's AI provider (Claude) to be read; every reading is logged with its cost, as the AI use policy requires.</p>
                </div>
            ) : !info.documents.length ? (
                <p className="text-sm text-slate-500">Upload the signed contract as a PDF in Documents above, then read it here.</p>
            ) : (
                <div className="flex flex-wrap gap-2 items-center">
                    <select value={docId} onChange={(e) => setDocId(e.target.value)} className="p-2 border rounded text-sm">
                        {info.documents.map((x) => <option key={x.document_id} value={x.document_id}>{x.document_name} ({x.document_type})</option>)}
                    </select>
                    <button onClick={read} disabled={busy || !docId} className="px-4 py-2 rounded bg-slate-800 text-white text-sm disabled:opacity-50">
                        {busy && !reading ? 'Reading… (up to a minute)' : 'Read this contract'}</button>
                </div>
            )}

            {reading && (
                <div className="border-t pt-4 space-y-3">
                    {d && (
                        <div className="text-sm text-slate-700">
                            <b>{d.contract_form || 'Contract'}</b>{d.property_address ? ` · ${d.property_address}` : ''}
                            {d.buyers && d.buyers.length > 0 && <span> · Buyer: {d.buyers.join(', ')}</span>}
                            {d.sellers && d.sellers.length > 0 && <span> · Seller: {d.sellers.join(', ')}</span>}
                            {d.fully_signed === false && <span className="ml-2 text-red-700 font-medium">Not fully signed</span>}
                            {reading.cost_usd != null && <span className="ml-2 text-xs text-slate-400">(reading cost ${Number(reading.cost_usd).toFixed(2)})</span>}
                        </div>
                    )}
                    {d && !d.is_purchase_contract && <p className="text-sm text-red-700">This document does not look like a purchase contract.</p>}
                    {d && (d.warnings || []).length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                            <b>Check by hand:</b><ul className="list-disc ml-5">{d.warnings.map((w) => <li key={w}>{w}</li>)}</ul></div>
                    )}
                    {(reading.proposals || []).length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="py-2 w-8" /><th>Item</th><th>On the deal now</th><th>From the contract</th><th>Contract says</th></tr></thead>
                                <tbody>
                                    {reading.proposals.map((p) => {
                                        const same = p.proposed != null && String(p.proposed) === String(p.current);
                                        return (
                                            <tr key={p.key} className="border-t border-slate-100 align-top">
                                                <td className="py-2"><input type="checkbox" disabled={p.proposed == null || reading.status === 'applied'} checked={picked.has(p.key)}
                                                    onChange={(e) => { const s = new Set(picked); if (e.target.checked) s.add(p.key); else s.delete(p.key); setPicked(s); }} /></td>
                                                <td className="py-2 pr-3">{p.label}</td>
                                                <td className="py-2 pr-3 text-slate-500">{show(p, p.current)}</td>
                                                <td className={`py-2 pr-3 font-medium ${same ? 'text-slate-400' : ''}`}>{show(p, p.proposed)}{same && ' (same)'}
                                                    {p.rule && <div className="text-xs text-slate-500 font-normal">{p.rule.days} {BASIS[p.rule.basis]} after acceptance</div>}
                                                    {p.note && <div className="text-xs text-amber-700 font-normal">{p.note}</div>}</td>
                                                <td className="py-2 text-xs text-slate-500">{p.quote ? `“${p.quote}”` : ''}{p.page ? ` (p. ${p.page})` : ''}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {d && (d.not_found || []).length > 0 && <p className="text-xs text-slate-500">Not found in the contract: {d.not_found.join(', ')}.</p>}
                    {d && (d.special_provisions || []).length > 0 && (
                        <div className="text-xs text-slate-600"><b>Special provisions:</b><ul className="list-disc ml-5">{d.special_provisions.map((s) => <li key={s}>{s}</li>)}</ul></div>
                    )}
                    {reading.status !== 'applied' && reading.status !== 'discarded' && (
                        <div className="flex gap-2">
                            <button onClick={apply} disabled={busy || !picked.size} className="px-4 py-2 rounded bg-emerald-700 text-white text-sm disabled:opacity-50">Accept {picked.size} ticked value(s)</button>
                            <button onClick={discard} className="px-4 py-2 rounded bg-slate-100 text-slate-700 text-sm">Discard</button>
                        </div>
                    )}
                </div>
            )}

            {info.readings.length > 0 && !reading && (
                <div className="text-xs text-slate-500">
                    Past readings: {info.readings.slice(0, 5).map((r) => (
                        <button key={r.extraction_id} onClick={() => r.status !== 'failed' && reopen(r.extraction_id)} className="mr-3 underline" title={r.error || ''}>
                            #{r.extraction_id} {r.status}{r.status === 'failed' ? '' : ` ($${(r.cost_microusd / 1e6).toFixed(2)})`}</button>))}
                </div>
            )}
        </div>
    );
}
